import { Card } from 'primereact/card';
import { Button } from 'src/redesign/components/Button';
import { TicketPack } from 'src/types/types';
import { API_URL } from 'src/utils/transport';

const urlSearchParams = new URLSearchParams(window.location.search);
let instance = urlSearchParams.get('instance');

if (!instance) {
  const hashFragment = window.location.hash.split('?')[1] || '';
  const hashParams = new URLSearchParams(hashFragment);
  instance = hashParams.get('instance');
}

type TicketPackCardProps = {
  pack: TicketPack;
  isLoading: boolean;
  hasActiveSubscription: boolean;
};

export const TicketPackCard = ({ pack, isLoading, hasActiveSubscription }: TicketPackCardProps) => {
  const isDisabled = !hasActiveSubscription || isLoading;
  return (
    <div className="col-12 lg:col-3 mb-4">
      <Card className="h-full">
        <div className="flex flex-column h-full" style={{ minHeight: '400px' }}>
          {/* Header */}
          <div className="mb-3">
            <h3 className="text-2xl font-bold mb-2">{pack.name}</h3>
            <p className="text-600 text-sm">{pack.description}</p>
          </div>

          {/* Price */}
          <div className="mb-4">
            <span className="text-4xl font-bold">${pack.price}</span>
            <span className="text-600 ml-2">one-time</span>
          </div>

          {/* Prominent Pack Information */}
          <div className="mb-3">
            <div className="p-2 bg-green-50 border-round border-2 border-green-200 mb-2">
              <div className="flex align-items-center justify-content-between">
                <div className="flex align-items-center">
                  <i className="pi pi-ticket mr-2 text-green-600" style={{ fontSize: '1rem' }}></i>
                  <span className="font-semibold text-700">Tickets:</span>
                </div>
                <span className="font-bold text-xl text-900">
                  {pack.ticket_quota.toLocaleString()}
                </span>
              </div>
            </div>

            <div className="p-2 bg-blue-50 border-round border-2 border-blue-200">
              <div className="flex align-items-center justify-content-between">
                <div className="flex align-items-center">
                  <i className="pi pi-tag mr-2 text-blue-600" style={{ fontSize: '1rem' }}></i>
                  <span className="font-semibold text-700">Max Price:</span>
                </div>
                <span className="font-bold text-xl text-900">
                  ${pack.max_ticket_price.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Additional Features */}
          <div className="flex flex-column gap-2 mb-4 flex-grow-1">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-check text-green-500" />
              <span>{pack.emails_included.toLocaleString()} emails</span>
            </div>
            <div className="flex align-items-center gap-2">
              <i className="pi pi-check text-green-500" />
              <span>Valid for {pack.expiry_days} days</span>
            </div>
          </div>

          {/* CTA - Form submit */}
          <div title={!hasActiveSubscription ? 'Subscription Required' : ''}>
            <form
              action={
                window.brand === 'wix'
                  ? `${API_URL}/api/v1/dashboard/billing/ticket-packs/purchase?instance=${instance}&brand=wix`
                  : `${API_URL}/api/v1/dashboard/billing/ticket-packs/purchase`
              }
              method="POST"
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <input type="hidden" name="packId" value={pack.pack_id} />
              <Button
                type="submit"
                disabled={isDisabled}
                className="w-full"
                priority="primary"
                size="medium"
                style={{
                  backgroundColor: isDisabled ? '#9ca3af' : 'var(--brand-background-color)',
                  borderColor: isDisabled ? '#9ca3af' : 'var(--brand-background-color)',
                  opacity: isDisabled ? 0.6 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                {isLoading ? 'Processing...' : 'Purchase Pack'}
              </Button>
            </form>
          </div>

          {!hasActiveSubscription && (
            <div className="mt-2 p-2 bg-orange-50 border-round text-center">
              <i className="pi pi-info-circle text-orange-500 mr-2" />
              <span className="text-orange-700 text-sm">Requires active subscription</span>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
